import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {getAutomatedReviewer, type AutomatedReviewer} from '@github-ui/hovercards/automated-reviewers'
import {getReviewerHovercardAttributes, type ReviewerHovercard} from '@github-ui/hovercards/reviewer-hovercard'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ownerPath, teamPath} from '@github-ui/paths'
import {Link as PrimerLink} from '@primer/react'
import type {Reviewer} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'
import styles from './Reviewers.module.css'
import {isCopilotPullRequestReviewer, isCopilotReviewer} from './copilot-reviewer'

type IdentityOptions = {
  withAvatar?: boolean
  withLeadingAt?: boolean
  // Render as an inline text link (honors the link-underline user preference and allows the name to wrap).
  // Use in running-text contexts where there is no leading avatar.
  inline?: boolean
  inlineAccent?: boolean
}

const COPILOT_DISPLAY_NAME = 'Copilot'

type ReviewerPresentation = {
  displayName: string
  href: string
  hovercard?: ReviewerHovercard
  copilot?: boolean
  square?: boolean
}

export function isAutomatedReviewBot(reviewer: Reviewer | undefined): boolean {
  return automatedReviewBot(reviewer) !== undefined
}

function automatedReviewBot(reviewer: Reviewer | undefined): AutomatedReviewer | undefined {
  if (!reviewer || reviewer.reviewerType === 'Team') return undefined
  if (!isFeatureEnabled('automated_review_comment_reviewer_identity')) return undefined

  return getAutomatedReviewer(reviewer.slug)
}

export function reviewerDisplayName(reviewer: Reviewer): string {
  return reviewerPresentation(reviewer, {preserveTeamMention: true}).displayName
}

export function ReviewerIdentity({
  reviewer,
  withAvatar = true,
  withLeadingAt = false,
  inline = false,
  inlineAccent = false,
}: {reviewer: Reviewer} & IdentityOptions) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const presentation = reviewerPresentation(reviewer, {preserveTeamMention: withLeadingAt})
  const hovercardAttrs = getReviewerHovercardAttributes(presentation.hovercard, getHovercardAttributesForType)

  return (
    <IdentityLink
      href={presentation.href}
      avatarUrl={withAvatar ? reviewer.avatarUrl : undefined}
      copilot={presentation.copilot}
      square={presentation.square}
      displayName={presentation.displayName}
      withLeadingAt={withLeadingAt}
      inline={inline}
      inlineAccent={inlineAccent}
      hovercardAttrs={hovercardAttrs}
    />
  )
}

function reviewerPresentation(
  reviewer: Reviewer,
  {preserveTeamMention = false}: {preserveTeamMention?: boolean} = {},
): ReviewerPresentation {
  if (reviewer.reviewerType === 'Team') {
    const teamSlug = reviewer.name || stripOrgPrefix(reviewer.displayLogin)
    const owner = reviewer.organizationLogin ?? ''

    return {
      displayName: preserveTeamMention ? reviewer.displayLogin : teamSlug,
      href: owner ? teamPath({owner, team: teamSlug}) : ownerPath({owner: reviewer.displayLogin}),
      hovercard: {type: 'team', owner, team: teamSlug},
      square: true,
    }
  }

  if (isCopilotReviewer(reviewer)) {
    const botSlug = reviewer.slug || reviewer.displayLogin
    const pullRequestReviewer = isCopilotPullRequestReviewer(reviewer)
    return {
      displayName: pullRequestReviewer ? COPILOT_DISPLAY_NAME : reviewer.displayLogin,
      href: `/apps/${botSlug}`,
      hovercard: {type: 'copilot', botSlug},
      copilot: pullRequestReviewer,
    }
  }

  const reviewBot = automatedReviewBot(reviewer)
  if (reviewBot) {
    return {
      displayName: reviewBot.displayName,
      href: `/apps/${reviewBot.slug}`,
      hovercard: isFeatureEnabled('automated_review_comment_reviewer_hovercard')
        ? {type: 'automated_reviewer', botSlug: reviewBot.slug}
        : undefined,
    }
  }

  if (reviewer.slug) {
    return {
      displayName: reviewer.name || reviewer.displayLogin,
      href: `/apps/${reviewer.slug}`,
    }
  }

  return {
    displayName: reviewer.displayLogin,
    href: ownerPath({owner: reviewer.displayLogin}),
    hovercard: {type: 'user', login: reviewer.displayLogin},
  }
}

function IdentityLink({
  href,
  avatarUrl,
  square = false,
  copilot = false,
  displayName,
  withLeadingAt,
  inline = false,
  inlineAccent,
  hovercardAttrs,
}: {
  href: string
  avatarUrl?: string
  square?: boolean
  copilot?: boolean
  displayName: string
  withLeadingAt: boolean
  inline?: boolean
  inlineAccent: boolean
  hovercardAttrs: ReturnType<ReturnType<typeof useGetHovercardAttributesForType>>
}) {
  return (
    <PrimerLink
      href={href}
      muted={inline && !inlineAccent ? true : undefined}
      inline={inline}
      className={
        inline
          ? `${styles.inlineReviewerIdentity}${inlineAccent ? ' Link--inTextBlock' : ''}`
          : `Link--primary ${styles.reviewerIdentity}`
      }
      {...hovercardAttrs}
    >
      {avatarUrl !== undefined && (
        <span className={styles.reviewerAvatar}>
          {copilot ? <CopilotAvatar isDecorative /> : <GitHubAvatar src={avatarUrl} size={20} square={square} />}
        </span>
      )}
      <span className={inline ? styles.inlineReviewerName : styles.reviewerName}>
        {withLeadingAt && <span>@</span>}
        {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
        {displayName}
      </span>
    </PrimerLink>
  )
}

// Team `displayLogin` values are prefixed with the org (e.g. `org/team`); strip the prefix to get the team slug.
function stripOrgPrefix(displayLogin: string): string {
  const slashIndex = displayLogin.lastIndexOf('/')
  return slashIndex === -1 ? displayLogin : displayLogin.slice(slashIndex + 1)
}
